import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Mail, Download, Pencil, Send, CheckCircle2, Clock, FileText, Settings2, RotateCcw, Users2, X } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft: { label: "Bozza", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: FileText },
  sent: { label: "Inviata", cls: "bg-sky-100 text-sky-800 border-sky-200", icon: Send },
  partially_signed: { label: "Firma parziale", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  signed: { label: "Firmata", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
};

/**
 * Splits company address into street (via) and city/CAP/province.
 * Companies model stores full_address as "VIA X 10 — 25087, SALO', BS, ITALIA".
 * Also honours c.meta.city / c.city / c.address_street when available.
 */
function splitAddress(c) {
  if (!c) return { via: "", citta: "" };
  const meta = c.meta || {};
  // Preferred explicit fields
  const explicitStreet = c.address_street || meta.street || null;
  const explicitCity = c.city || meta.city || null;
  const explicitCap = meta.cap || null;
  const explicitProv = meta.province || null;
  const fullAddress = c.address || "";
  let via = explicitStreet || "";
  let citta = "";
  if (fullAddress.includes(" — ")) {
    const parts = fullAddress.split(" — ");
    if (!via) via = (parts[0] || "").trim();
    citta = (parts.slice(1).join(" — ") || "").trim();
  } else if (!via) {
    via = fullAddress.trim();
  }
  if (explicitCity) {
    citta = [explicitCap, explicitCity, explicitProv].filter(Boolean).join(", ") || explicitCity;
  }
  return { via, citta };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function NominationsPage() {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "super_admin";

  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const todayIt = () => new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  const [form, setForm] = useState({
    template_key: "", company_id: "", worker_id: "",
    azienda: "", datore_lavoro: "", lavoratore: "",
    p_iva: "", cf_azienda: "", cf_datore: "", cf_lavoratore: "",
    sede_citta: "", sede_via: "", attivita: "",
    luogo_nascita_datore: "", data_nascita_datore: "",
    luogo: "", data_nomina: todayIt(),
  });
  const [saving, setSaving] = useState(false);

  const [sendDialog, setSendDialog] = useState(null); // nomination
  const [sendEmail, setSendEmail] = useState("");
  const [sendSignerType, setSendSignerType] = useState("worker");
  const [sending, setSending] = useState(false);

  const [deleteDoc, setDeleteDoc] = useState(null);

  // Templates editor
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);

  // Batch dialog
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({
    template_key: "", company_id: "", worker_ids: [],
    azienda: "", datore_lavoro: "", p_iva: "", cf_azienda: "",
    sede_citta: "", sede_via: "", attivita: "", luogo: "", data_nomina: "",
    send_immediately: false,
  });
  const [batchWorkers, setBatchWorkers] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);

  const load = () => api.get("/nominations").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/nominations/templates").then((r) => setTemplates(r.data));
    api.get("/companies").then((r) => setCompanies(Array.isArray(r.data) ? r.data : r.data?.items || []));
  }, []);

  useEffect(() => {
    if (!form.company_id) return setWorkers([]);
    api.get(`/workers?company_id=${form.company_id}`).then((r) => setWorkers(r.data));
    // Pre-fill azienda con parsing intelligente indirizzo
    const c = companies.find((x) => x.id === form.company_id);
    if (c) {
      const { via, citta } = splitAddress(c);
      setForm((f) => ({ ...f, azienda: c.name, p_iva: c.vat || "", cf_azienda: c.tax_code || "", sede_citta: citta, sede_via: via }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.company_id]);

  useEffect(() => {
    if (!form.worker_id) return;
    const w = workers.find((x) => x.id === form.worker_id);
    if (w) setForm((f) => ({ ...f, lavoratore: `${w.first_name || ""} ${w.last_name || ""}`.trim(), cf_lavoratore: w.tax_code || "" }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.worker_id]);

  const companyName = useMemo(() => {
    const m = new Map(); companies.forEach((c) => m.set(c.id, c.name));
    return (id) => (id ? m.get(id) || "—" : "—");
  }, [companies]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.template_key) return toast.error("Seleziona il tipo di nomina");
    if (!form.company_id) return toast.error("Seleziona l'azienda");
    setSaving(true);
    try {
      const { template_key, company_id, worker_id, ...fields } = form;
      await api.post("/nominations", {
        template_key, company_id, worker_id: worker_id || null, fields,
      });
      toast.success("Lettera di nomina generata");
      setCreateOpen(false);
      setForm({ ...form, template_key: "", company_id: "", worker_id: "", lavoratore: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
    setSaving(false);
  };

  const doSend = async () => {
    if (!sendDialog) return;
    if (!sendEmail || !sendEmail.includes("@")) return toast.error("Email non valida");
    setSending(true);
    try {
      await api.post(`/nominations/${sendDialog.id}/send-signature`, { to_email: sendEmail, signer_type: sendSignerType });
      const roleLabel = sendSignerType === "datore" ? "Datore di Lavoro" : "Lavoratore Nominato";
      toast.success(`Link firma ${roleLabel} inviato a ${sendEmail}`);
      setSendDialog(null);
      setSendEmail("");
      setSendSignerType("worker");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    }
    setSending(false);
  };

  const confirmDelete = async () => {
    if (!deleteDoc) return;
    try {
      await api.delete(`/nominations/${deleteDoc.id}`);
      toast.success("Eliminata");
      setDeleteDoc(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
      setDeleteDoc(null);
    }
  };

  const saveTemplate = async () => {
    if (!tplEditing) return;
    try {
      await api.patch(`/nominations/templates/${tplEditing.key}`, {
        label: tplEditing.label, body: tplEditing.body, normativa: tplEditing.normativa,
      });
      toast.success("Modello aggiornato");
      const r = await api.get("/nominations/templates");
      setTemplates(r.data);
      setTplEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const resetTemplate = async () => {
    if (!tplEditing) return;
    if (!window.confirm("Ripristinare il template al default? Le modifiche verranno perse.")) return;
    try {
      const r = await api.post(`/nominations/templates/${tplEditing.key}/reset`);
      setTplEditing(r.data);
      const lr = await api.get("/nominations/templates");
      setTemplates(lr.data);
      toast.success("Template ripristinato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  // Batch load workers when company changes
  useEffect(() => {
    if (!batchForm.company_id) return setBatchWorkers([]);
    api.get(`/workers?company_id=${batchForm.company_id}`).then((r) => setBatchWorkers(r.data));
    const c = companies.find((x) => x.id === batchForm.company_id);
    if (c) {
      const { via, citta } = splitAddress(c);
      setBatchForm((f) => ({ ...f, azienda: c.name, p_iva: c.vat || "", cf_azienda: c.tax_code || "", sede_citta: citta, sede_via: via }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchForm.company_id]);

  const toggleBatchWorker = (wid) => {
    setBatchForm((f) => ({
      ...f,
      worker_ids: f.worker_ids.includes(wid)
        ? f.worker_ids.filter((x) => x !== wid)
        : [...f.worker_ids, wid],
    }));
  };

  const selectAllBatchWorkers = () => {
    setBatchForm((f) => ({ ...f, worker_ids: batchWorkers.map((w) => w.id) }));
  };

  const submitBatch = async (e) => {
    e.preventDefault();
    if (!batchForm.template_key) return toast.error("Seleziona il tipo di nomina");
    if (!batchForm.company_id) return toast.error("Seleziona l'azienda");
    if (!batchForm.worker_ids.length) return toast.error("Seleziona almeno un lavoratore");
    // Se send_immediately: warn se ci sono worker senza email
    if (batchForm.send_immediately) {
      const noEmail = batchForm.worker_ids
        .map((id) => batchWorkers.find((w) => w.id === id))
        .filter((w) => w && !w.email);
      if (noEmail.length) {
        const ok = window.confirm(
          `${noEmail.length} lavoratore/i non ha email: ${noEmail.map((w) => `${w.first_name} ${w.last_name}`).join(", ")}\n\nLa nomina verrà creata ma NON inviata via email. Procedere comunque?`
        );
        if (!ok) return;
      }
    }
    setBatchRunning(true);
    try {
      const { template_key, company_id, worker_ids, send_immediately, ...common_fields } = batchForm;
      const r = await api.post("/nominations/batch", {
        template_key, company_id, worker_ids, common_fields, send_immediately,
      });
      const d = r.data;
      toast.success(
        `${d.created.length}/${d.total_requested} nomine create` +
        (send_immediately ? ` · ${d.sent_count} email inviate` : "")
      );
      if (d.errors?.length) {
        toast.warning(`${d.errors.length} problemi — vedi console`);
        console.warn("Batch errors:", d.errors);
      }
      setBatchOpen(false);
      setBatchForm({ ...batchForm, worker_ids: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore batch");
    }
    setBatchRunning(false);
  };

  return (
    <div className="space-y-6" data-testid="nominations-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lettere di Nomina</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Genera lettere ufficiali (10 modelli disponibili), invia link per firma elettronica al cliente, archivio storico.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setTplOpen(true)} className="rounded-full btn-lift" data-testid="nom-manage-templates">
              <Settings2 className="w-4 h-4 mr-1.5" /> Gestisci modelli
            </Button>
          )}
          <Button variant="outline" onClick={async () => {
            try {
              const r = await api.post("/nominations/reminders/run-partial");
              toast.success(`Solleciti inviati: ${r.data.sent} · saltati: ${r.data.skipped}${r.data.errors?.length ? ` · errori: ${r.data.errors.length}` : ""}`);
            } catch (err) {
              toast.error(err.response?.data?.detail || "Errore invio solleciti");
            }
          }} className="rounded-full btn-lift" data-testid="nom-partial-remind">
            <Clock className="w-4 h-4 mr-1.5" /> Solleciti firma
          </Button>
          <Button variant="outline" onClick={() => { setBatchForm((f) => ({ ...f, data_nomina: f.data_nomina || todayIt() })); setBatchOpen(true); }} className="rounded-full btn-lift" data-testid="nom-batch">
            <Users2 className="w-4 h-4 mr-1.5" /> Batch nomine
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-lift" data-testid="nom-create">
                <Plus className="w-4 h-4 mr-1.5" /> Nuova nomina
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nuova lettera di nomina</DialogTitle>
                <DialogDescription>I campi vuoti verranno mostrati come "____" nel documento e potranno essere compilati a mano o in una seconda fase.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Tipo di nomina</Label>
                  <Select value={form.template_key} onValueChange={(v) => setForm({ ...form, template_key: v })}>
                    <SelectTrigger data-testid="nom-tpl-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Azienda (cliente)</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="nom-company-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nome azienda (come apparirà)</Label>
                  <Input value={form.azienda} onChange={(e) => setForm({ ...form, azienda: e.target.value })} />
                </div>
                <div>
                  <Label>P.IVA</Label>
                  <Input value={form.p_iva} onChange={(e) => setForm({ ...form, p_iva: e.target.value })} />
                </div>
                <div>
                  <Label>Sede — Città</Label>
                  <Input value={form.sede_citta} onChange={(e) => setForm({ ...form, sede_citta: e.target.value })} />
                </div>
                <div>
                  <Label>Sede — Via</Label>
                  <Input value={form.sede_via} onChange={(e) => setForm({ ...form, sede_via: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Datore di Lavoro (nome cognome)</Label>
                  <Input value={form.datore_lavoro} onChange={(e) => setForm({ ...form, datore_lavoro: e.target.value })} />
                </div>
                <div>
                  <Label>CF Datore di Lavoro</Label>
                  <Input value={form.cf_datore} onChange={(e) => setForm({ ...form, cf_datore: e.target.value })} />
                </div>
                <div>
                  <Label>Luogo/data nascita datore</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Luogo" value={form.luogo_nascita_datore} onChange={(e) => setForm({ ...form, luogo_nascita_datore: e.target.value })} />
                    <Input placeholder="Data" value={form.data_nascita_datore} onChange={(e) => setForm({ ...form, data_nascita_datore: e.target.value })} />
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Lavoratore da nominare (opzionale)</Label>
                  <Select value={form.worker_id || "__none__"} onValueChange={(v) => setForm({ ...form, worker_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Nessuno —</SelectItem>
                      {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.first_name} {w.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nome lavoratore (override)</Label>
                  <Input value={form.lavoratore} onChange={(e) => setForm({ ...form, lavoratore: e.target.value })} />
                </div>
                <div>
                  <Label>CF Lavoratore</Label>
                  <Input value={form.cf_lavoratore} onChange={(e) => setForm({ ...form, cf_lavoratore: e.target.value })} />
                </div>
                <div>
                  <Label>Attività</Label>
                  <Input value={form.attivita} onChange={(e) => setForm({ ...form, attivita: e.target.value })} />
                </div>
                <div>
                  <Label>Luogo emissione</Label>
                  <Input value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} placeholder={form.sede_citta} data-testid="nom-luogo" />
                </div>
                <div>
                  <Label>Data nomina</Label>
                  <Input value={form.data_nomina} onChange={(e) => setForm({ ...form, data_nomina: e.target.value })} placeholder="gg/mm/aaaa" data-testid="nom-data" />
                </div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" disabled={saving} className="rounded-full btn-lift" data-testid="nom-submit">
                    {saving ? "Generazione…" : "Genera lettera"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-3">Tipo</th>
              <th className="text-left px-5 py-3">Azienda</th>
              <th className="text-left px-5 py-3">Lavoratore</th>
              <th className="text-left px-5 py-3">Stato</th>
              <th className="text-left px-5 py-3">Data</th>
              <th className="text-right px-5 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {list.map((n) => {
              const meta = STATUS_META[n.status] || STATUS_META.draft;
              const Icon = meta.icon;
              return (
                <tr key={n.id} className="border-t border-border hover:bg-muted/40" data-testid={`nom-row-${n.id}`}>
                  <td className="px-5 py-3 font-medium">{n.template_label}</td>
                  <td className="px-5 py-3 text-muted-foreground">
                    <Link to={`/companies/${n.company_id}`} className="hover:text-primary hover:underline">{companyName(n.company_id)}</Link>
                  </td>
                  <td className="px-5 py-3">{n.fields?.lavoratore || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-0.5 rounded border ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {n.sent_to && <div className="text-[10px] text-muted-foreground mt-0.5">→ {n.sent_to}</div>}
                    {n.signed_by_name && <div className="text-[10px] text-emerald-700 mt-0.5">✍ {n.signed_by_name} · {fmtDate(n.signed_at)}</div>}
                  </td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">
                    <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(n.created_at)}</div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <a href={`${BACKEND}${n.signed_file_url || n.draft_file_url}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary btn-lift" title="Scarica PDF" data-testid={`nom-download-${n.id}`}>
                        <Download className="w-4 h-4" />
                      </a>
                      {n.status !== "signed" && (
                        <button onClick={() => { setSendDialog(n); setSendEmail(""); }} className="text-muted-foreground hover:text-primary btn-lift" title="Invia per firma" data-testid={`nom-send-${n.id}`}>
                          <Mail className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => setDeleteDoc(n)} className="text-muted-foreground hover:text-destructive btn-lift" title="Elimina" data-testid={`nom-delete-${n.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground italic">Nessuna lettera di nomina generata.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Send-signature dialog */}
      <Dialog open={!!sendDialog} onOpenChange={(o) => !o && setSendDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia per firma</DialogTitle>
            <DialogDescription>
              Seleziona chi deve firmare e inserisci la sua email. Riceverà un link personale che apre il documento nel browser — nessuna app da installare. Puoi inviare due email separate (una al Datore, una al Lavoratore) usando questo dialogo due volte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Chi deve firmare</Label>
              <Select value={sendSignerType} onValueChange={setSendSignerType}>
                <SelectTrigger data-testid="nom-send-signer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="datore">Datore di Lavoro</SelectItem>
                  <SelectItem value="worker">Lavoratore Nominato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email destinatario</Label>
              <Input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder={sendSignerType === "datore" ? "datore@azienda.it" : "lavoratore@azienda.it"} data-testid="nom-send-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setSendDialog(null)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending} className="rounded-full btn-lift" data-testid="nom-send-confirm">
              {sending ? "Invio…" : `Invia a ${sendSignerType === "datore" ? "Datore" : "Lavoratore"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteDoc} onOpenChange={(o) => !o && setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la nomina?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare la lettera "<b>{deleteDoc?.template_label}</b>". Se già firmata, il documento resterà comunque in archivio Documenti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch nominations dialog */}
      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Batch nomine — N lavoratori in un click</DialogTitle>
            <DialogDescription>
              Genera lo stesso tipo di nomina per più lavoratori. I dati specifici del lavoratore
              (nome, CF) vengono compilati automaticamente. Opzionale: invia subito la mail di firma
              all'indirizzo email del lavoratore.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBatch} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Tipo di nomina</Label>
              <Select value={batchForm.template_key} onValueChange={(v) => setBatchForm({ ...batchForm, template_key: v })}>
                <SelectTrigger data-testid="nom-batch-tpl"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Azienda</Label>
              <Select value={batchForm.company_id} onValueChange={(v) => setBatchForm({ ...batchForm, company_id: v, worker_ids: [] })}>
                <SelectTrigger data-testid="nom-batch-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Datore di lavoro</Label>
              <Input value={batchForm.datore_lavoro} onChange={(e) => setBatchForm({ ...batchForm, datore_lavoro: e.target.value })} data-testid="nom-batch-datore" />
            </div>
            <div>
              <Label>Luogo emissione</Label>
              <Input value={batchForm.luogo} onChange={(e) => setBatchForm({ ...batchForm, luogo: e.target.value })} placeholder={batchForm.sede_citta} data-testid="nom-batch-luogo" />
            </div>
            <div>
              <Label>Data nomina</Label>
              <Input value={batchForm.data_nomina} onChange={(e) => setBatchForm({ ...batchForm, data_nomina: e.target.value })} placeholder="gg/mm/aaaa" data-testid="nom-batch-data" />
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label>Lavoratori da nominare ({batchForm.worker_ids.length} selezionati)</Label>
                {batchWorkers.length > 0 && (
                  <button type="button" onClick={selectAllBatchWorkers} className="text-xs text-primary hover:underline" data-testid="nom-batch-selectall">
                    Seleziona tutti ({batchWorkers.length})
                  </button>
                )}
              </div>
              <div className="border border-border rounded-md max-h-64 overflow-y-auto divide-y divide-border" data-testid="nom-batch-workers-list">
                {batchWorkers.length === 0 ? (
                  <div className="p-4 text-xs text-muted-foreground italic text-center">
                    {batchForm.company_id ? "Nessun lavoratore in questa azienda." : "Seleziona prima un'azienda."}
                  </div>
                ) : batchWorkers.map((w) => {
                  const checked = batchForm.worker_ids.includes(w.id);
                  return (
                    <label key={w.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40 ${checked ? "bg-primary/5" : ""}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleBatchWorker(w.id)} className="w-4 h-4" data-testid={`nom-batch-worker-${w.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{w.first_name} {w.last_name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {w.role || "—"} · {w.email || <span className="text-amber-600">nessuna email</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2 border-t border-border pt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={batchForm.send_immediately} onChange={(e) => setBatchForm({ ...batchForm, send_immediately: e.target.checked })} className="w-4 h-4" data-testid="nom-batch-send-now" />
                <span className="text-sm">Invia subito la mail di firma a ciascun lavoratore (usa la sua email registrata)</span>
              </label>
            </div>
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setBatchOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={batchRunning} className="rounded-full btn-lift" data-testid="nom-batch-submit">
                {batchRunning ? "Generazione…" : `Genera ${batchForm.worker_ids.length || ""} nomine`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Templates manager */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modelli lettere di nomina</DialogTitle>
            <DialogDescription>Placeholder disponibili: <code>{"{{azienda}}"}</code>, <code>{"{{datore_lavoro}}"}</code>, <code>{"{{lavoratore}}"}</code>, <code>{"{{p_iva}}"}</code>, <code>{"{{sede_citta}}"}</code>, <code>{"{{sede_via}}"}</code>, <code>{"{{data_nomina}}"}</code>, <code>{"{{luogo}}"}</code>, ecc.</DialogDescription>
          </DialogHeader>
          {tplEditing ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Button variant="outline" size="sm" onClick={() => setTplEditing(null)}>← Torna alla lista</Button>
                <Button variant="outline" size="sm" onClick={resetTemplate} data-testid="nom-tpl-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina default</Button>
              </div>
              <div>
                <Label>Titolo</Label>
                <Input value={tplEditing.label} onChange={(e) => setTplEditing({ ...tplEditing, label: e.target.value })} data-testid="nom-tpl-label" />
              </div>
              <div>
                <Label>Riferimento normativo</Label>
                <Input value={tplEditing.normativa || ""} onChange={(e) => setTplEditing({ ...tplEditing, normativa: e.target.value })} data-testid="nom-tpl-normativa" />
              </div>
              <div>
                <Label>Testo modello</Label>
                <Textarea rows={20} value={tplEditing.body} onChange={(e) => setTplEditing({ ...tplEditing, body: e.target.value })} className="font-mono text-xs" data-testid="nom-tpl-body" />
              </div>
              <DialogFooter>
                <Button onClick={saveTemplate} className="rounded-full btn-lift" data-testid="nom-tpl-save">Salva modifiche</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="divide-y divide-border border border-border rounded-md">
              {templates.map((t) => (
                <div key={t.key} className="px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.label}</div>
                    <div className="text-xs text-muted-foreground">{t.normativa}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setTplEditing(t)} data-testid={`nom-tpl-edit-${t.key}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Modifica</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
